import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { db, auth } from "../firebase";
import { doc, getDoc, collection, query, where, getDocs } from "firebase/firestore";
import { getISTDate } from "../utils/dateUtils";
import { onAuthStateChanged } from "firebase/auth";
import Layout from "../components/Layout";
import { 
    FaStore, 
    FaUserTie, 
    FaChartLine, 
    FaCalendarDay, 
    FaCalendarAlt, 
    FaCubes, 
    FaPercentage,
    FaFileInvoice
} from "react-icons/fa";

const RsmDashboard = () => {
    const navigate = useNavigate();
    const [userProfile, setUserProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [statsLoading, setStatsLoading] = useState(true);

    const [dailyData, setDailyData] = useState({ uniqueOutlets: 0, activeDs: 0, avgPerDs: 0 });
    const [monthlyData, setMonthlyData] = useState({ uniqueOutlets: 0, activeDs: 0, avgPerDs: 0 });
    const [productivityData, setProductivityData] = useState({ totalBills: 0, uniqueOutlets: 0, avgBillsPerOutlet: 0 });

    useEffect(() => {
        const unsub = onAuthStateChanged(auth, async (user) => {
            if (!user) { navigate("/"); return; }
            const snap = await getDoc(doc(db, "users", user.uid));
            if (snap.exists()) {
                const profile = snap.data();
                if (profile.role !== "rsm") {
                    navigate("/");
                    return;
                }
                setUserProfile(profile);
                loadAnalytics(profile);
            } else {
                navigate("/");
            }
            setLoading(false);
        });
        return () => unsub();
    }, [navigate]);

    const loadAnalytics = async (profile) => {
        setStatsLoading(true);
        try {
            const today = getISTDate();
            const firstOfMonth = today.slice(0, 8) + "01";

            // HIGH VOLUME OPTIMIZATION: Query pre-aggregated daily stock shards (ds_stock_daily)
            // This reduces the read load from potentially 200,000+ orders to just ~3,000 stock documents per month.
            
            // 1. Fetch Today's Stock Documents (One per Active DS)
            const qToday = query(
                collection(db, "ds_stock_daily"),
                where("clientId", "==", profile.clientId),
                where("date", "==", today)
            );
            const snapToday = await getDocs(qToday);
            const todayStocks = snapToday.docs.map(d => d.data());

            const tActiveStocks = todayStocks.filter(s => (s.billedOutlets || []).length > 0);
            const tOutletsSet = new Set();
            tActiveStocks.forEach(s => {
                (s.billedOutlets || []).forEach(oid => tOutletsSet.add(oid));
            });
            const tActiveDs = tActiveStocks.length; 
            const tAvg = tActiveDs > 0 ? (tOutletsSet.size / tActiveDs).toFixed(1) : 0;

            setDailyData({
                uniqueOutlets: tOutletsSet.size,
                activeDs: tActiveDs,
                avgPerDs: tAvg
            });

            // 2. Fetch Monthly Stock Documents (MTD)
            // Range query on different fields with equality on clientId requires composite indexing.
            // Using clientId only then filtering by date in-memory for immediate reliability.
            const qMonth = query(
                collection(db, "ds_stock_daily"),
                where("clientId", "==", profile.clientId)
            );
            const snapMonth = await getDocs(qMonth);
            const allMonthDocs = snapMonth.docs.map(d => d.data());
            const monthStocks = allMonthDocs.filter(d => d.date >= firstOfMonth && d.date <= today);

            const mOutletsSet = new Set();
            const mDsIdsSet = new Set();
            let mTotalBills = 0;

            monthStocks.forEach(s => {
                const billedCount = (s.billedOutlets || []).length;
                if (billedCount > 0) {
                    mDsIdsSet.add(s.dsId);
                    // Use totalBills if available, fallback to unique billedOutlets count for historical accuracy
                    mTotalBills += (s.totalBills || billedCount); 
                    (s.billedOutlets || []).forEach(oid => mOutletsSet.add(oid));
                }
            });

            const mActiveDs = mDsIdsSet.size;
            const mAvg = mActiveDs > 0 ? (mOutletsSet.size / mActiveDs).toFixed(1) : 0;

            setMonthlyData({
                uniqueOutlets: mOutletsSet.size,
                activeDs: mActiveDs,
                avgPerDs: mAvg
            });

            // 3. Efficiency / Productivity
            const pAvg = mOutletsSet.size > 0 ? (mTotalBills / mOutletsSet.size).toFixed(2) : 0;

            setProductivityData({
                totalBills: mTotalBills,
                uniqueOutlets: mOutletsSet.size,
                avgBillsPerOutlet: pAvg
            });

        } catch (err) {
            console.error("Error loading analytics:", err);
        } finally {
            setStatsLoading(false);
        }
    };

    if (loading) return (
        <Layout>
            <div style={{ padding: 100, textAlign: "center", color: "var(--text-muted)" }}>
                Initializing Strategic Hub...
            </div>
        </Layout>
    );

    const StatCard = ({ icon: Icon, label, value, subtext, color = "var(--primary)" }) => (
        <div className="premium-card" style={{ padding: "24px", position: "relative", overflow: "hidden" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
                <div style={{ 
                    width: 44, height: 44, borderRadius: 12, 
                    background: `rgba(${color === "var(--primary)" ? "255, 107, 0" : "16, 185, 129"}, 0.1)`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    color: color
                }}>
                    <Icon style={{ fontSize: 20 }} />
                </div>
            </div>
            <div style={{ fontSize: 32, fontWeight: 900, color: "var(--text-main)", marginBottom: 4 }}>
                {statsLoading ? "..." : value}
            </div>
            <div style={{ fontSize: 11, fontWeight: 800, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                {label}
            </div>
            {subtext && (
                <div style={{ fontSize: 10, color: "var(--text-muted)", marginTop: 8, fontStyle: "italic" }}>
                    {subtext}
                </div>
            )}
        </div>
    );

    const SectionHeader = ({ icon: Icon, title, subtitle }) => (
        <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 24, marginTop: 16 }}>
            <div style={{ color: "var(--primary)", fontSize: 24 }}><Icon /></div>
            <div>
                <h2 style={{ fontSize: 20, fontWeight: 900, margin: 0, letterSpacing: "-0.02em" }}>{title}</h2>
                <p style={{ color: "var(--text-muted)", fontSize: 12, margin: 0 }}>{subtitle}</p>
            </div>
        </div>
    );

    return (
        <Layout>
            <div className="layout-content">
                <div style={{ marginBottom: 48, display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
                    <div>
                        <h1 style={{ fontSize: 40, fontWeight: 900, marginBottom: 8, letterSpacing: "-0.03em" }}>STRATEGIC COMMAND</h1>
                        <p style={{ color: "var(--text-muted)", fontSize: 18, maxWidth: 600 }}>
                            Real-time field intelligence and regional performance matrices.
                        </p>
                    </div>
                    <div style={{ background: "var(--surface-hover)", padding: "12px 24px", borderRadius: 12, border: "1px solid var(--border)" }}>
                        <div style={{ fontSize: 10, fontWeight: 800, color: "var(--text-muted)", textTransform: "uppercase" }}>Current Audit Period</div>
                        <div style={{ fontSize: 14, fontWeight: 800, color: "var(--primary)" }}>{new Date().toLocaleString('default', { month: 'long', year: 'numeric' }).toUpperCase()}</div>
                    </div>
                </div>

                {/* 1. DAILY INTELLIGENCE */}
                <SectionHeader icon={FaCalendarDay} title="DAILY INTELLIGENCE" subtitle="Field activity and deployment status for today" />
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 24, marginBottom: 48 }}>
                    <StatCard icon={FaStore} label="Outlets Billed Today" value={dailyData.uniqueOutlets} />
                    <StatCard icon={FaUserTie} label="Active DSs Today" value={dailyData.activeDs} />
                    <StatCard icon={FaChartLine} label="Avg Outlets / DS" value={dailyData.avgPerDs} subtext={`Ratio: ${dailyData.uniqueOutlets} / ${dailyData.activeDs}`} />
                </div>

                {/* 2. MONTHLY PERFORMANCE */}
                <SectionHeader icon={FaCalendarAlt} title="MONTHLY PERFORMANCE" subtitle="Consolidated monthly reach and team efficiency (MTD)" />
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 24, marginBottom: 48 }}>
                    <StatCard icon={FaStore} label="Unique Outlets (MTD)" value={monthlyData.uniqueOutlets} color="#10b981" />
                    <StatCard icon={FaUserTie} label="Active DSs (MTD)" value={monthlyData.activeDs} color="#10b981" />
                    <StatCard icon={FaPercentage} label="Avg Monthly Reach / DS" value={monthlyData.avgPerDs} color="#10b981" subtext={`Ratio: ${monthlyData.uniqueOutlets} / ${monthlyData.activeDs}`} />
                </div>

                {/* 3. PRODUCTIVITY ANALYTICS */}
                <SectionHeader icon={FaCubes} title="PRODUCTIVITY ANALYTICS" subtitle="Order frequency and conversion depth analysis" />
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 24, marginBottom: 48 }}>
                    <StatCard icon={FaFileInvoice} label="Total Number of Bills" value={productivityData.totalBills} />
                    <StatCard icon={FaStore} label="Unique Outlets Billed" value={productivityData.uniqueOutlets} />
                    <StatCard icon={FaChartLine} label="Average Bills / Outlet" value={productivityData.avgBillsPerOutlet} subtext={`Ratio: ${productivityData.totalBills} / ${productivityData.uniqueOutlets}`} />
                </div>

                <div style={{ marginTop: 60, padding: 32, background: "#f8fafc", borderRadius: 16, border: "1px solid var(--border)", display: "flex", gap: 24, alignItems: "center" }}>
                    <div style={{ fontSize: 32 }}>💡</div>
                    <p style={{ color: "var(--text-muted)", fontSize: 13, lineHeight: 1.6, margin: 0 }}>
                        <strong style={{ color: "var(--text-main)" }}>System Note:</strong> These analytics are calculated in real-time from the field deployment stream. 
                        To optimize performance during high-volume cycles, consider implementing server-side aggregation for historical audit trails.
                    </p>
                </div>
            </div>
        </Layout>
    );
};

export default RsmDashboard;
